import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';
import { fetchHelper } from '../helpers/fetchHelper';
import { isLoggedIn as selectIsLoggedIn } from '../selectors';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

export enum VerificationStage {
    LOADING,
    WAITING_USER_VERIFICATION,
    CHECKS_PENDING,
    ALL_COMPLETE,
    ERROR,
}

interface VerificationState {
    clientId: number | null;
    mobileVerified: boolean | null;
    emailVerified: boolean | null;
    email: string;
    mobile: string;
    nextEmailAttemptTime: string | null;
    nextSmsAttemptTime: string | null;
    isAccountApproved: boolean | null;
    verificationStage: VerificationStage;
    isLoading: boolean;
    error: string | null;
    fetchVerificationState: () => void;
    resendVerification: () => Promise<void>;
}

interface VerificationApiResponse {
    clientId: number;
    mobileVerified: boolean;
    emailVerified: boolean;
    email: string;
    mobile: string;
    nextEmailAttemptTime: string;
    nextSmsAttemptTime: string;
    isAccountApproved: boolean;
    verificationStage: VerificationStage;
}

const VerificationContext = createContext<VerificationState | undefined>(undefined);

interface VerificationProviderProps {
    children: ReactNode;
    isLoggedIn: boolean;
}
const VerificationProviderComponent: React.FC<VerificationProviderProps> = ({ children, isLoggedIn }) => {
    const location = useLocation();
    const excludedRoutes = [
        '/signup',
        '/login',
        '/forgot-password',
        '/reset-password',
        '/privacy-policy'
    ];
    
    const isExcludedRoute = excludedRoutes.some(route => location.pathname.startsWith(route));
    
    const [clientId, setClientId] = useState<number | null>(null);
    const [mobileVerified, setMobileVerified] = useState<boolean | null>(null);
    const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
    const [email, setEmail] = useState<string>('');
    const [mobile, setMobile] = useState<string>('');
    const [nextEmailAttemptTime, setNextEmailAttemptTime] = useState<string | null>(null);
    const [nextSmsAttemptTime, setNextSmsAttemptTime] = useState<string | null>(null);
    const [isAccountApproved, setIsAccountApproved] = useState<boolean | null>(null);
    const [verificationStage, setVerificationStage] = useState<VerificationStage>(VerificationStage.LOADING);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchState = useCallback(async () => {
        if (!isLoggedIn || isExcludedRoute) {
             // Reset state if logged out or on excluded route
            setVerificationStage(VerificationStage.LOADING);
            setMobileVerified(null);
            setEmailVerified(null);
            setIsAccountApproved(null);
            setClientId(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await fetchHelper.getJson<VerificationApiResponse>('/api/users/verificationstate');
            if (response && response.data) {
                setClientId(response.data.clientId);
                setMobileVerified(response.data.mobileVerified);
                setEmailVerified(response.data.emailVerified);
                setEmail(response.data.email);
                setMobile(response.data.mobile);
                setNextEmailAttemptTime(response.data.nextEmailAttemptTime);
                setNextSmsAttemptTime(response.data.nextSmsAttemptTime);
                setIsAccountApproved(response.data.isAccountApproved);
                setVerificationStage(response.data.verificationStage);
            } else {
                 throw new Error('Verification state response missing data');
            }
        } catch (err: any) {
            console.error('Error fetching verification state:', err);
            setError(err.message || 'Failed to fetch verification status');
            setVerificationStage(VerificationStage.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [isLoggedIn, isExcludedRoute]); // Dependency: re-fetch if login status changes or route changes

    // Fetch state on mount and when login status changes
    useEffect(() => {
        fetchState();
    }, [fetchState]);

    useEffect(() => {
        if (!isLoggedIn || isExcludedRoute) return; // Don't poll if not logged in or on excluded route

        const intervalId = setInterval(() => {
            // Only poll if not already loading and not fully complete
            if (!isLoading && verificationStage !== VerificationStage.ALL_COMPLETE) {
                 fetchState();
            }
        }, 30000); // Poll every 30 seconds

        return () => clearInterval(intervalId); // Cleanup interval
    }, [isLoggedIn, isLoading, verificationStage, fetchState, isExcludedRoute]); // Re-evaluate polling if these change

    // Function to handle resend API call
    const resendVerification = useCallback(async () => {
        if (!clientId) {
            console.error("Cannot resend verification without Client ID");
            return;
        }
        setIsLoading(true);
        try {
            await fetchHelper.postJson('/api/users/resendverification', { clientId });
            // Immediately refresh the state after successful resend request
            await fetchState();
        } catch (err: any) {
            console.error('Error resending verification:', err);
            setError(err.message || 'Failed to resend verification');
            setIsLoading(false);
        }
    }, [clientId, fetchState]);


    const value: VerificationState = {
        clientId,
        mobileVerified,
        emailVerified,
        email,
        mobile,
        nextEmailAttemptTime,
        nextSmsAttemptTime,
        isAccountApproved,
        verificationStage,
        isLoading,
        error,
        fetchVerificationState: fetchState,
        resendVerification,
    };

    return (
        <VerificationContext.Provider value={value}>
            {children}
        </VerificationContext.Provider>
    );
};

// Connect the provider to Redux to get the isLoggedIn status (didnt think we were still using this!)
const mapStateToProps = (state: any) => ({
    isLoggedIn: selectIsLoggedIn(state),
});
export const VerificationProvider = connect(mapStateToProps)(VerificationProviderComponent);

export const useVerification = (): VerificationState => {
    const context = useContext(VerificationContext);
    if (context === undefined) {
        throw new Error('useVerification must be used within a VerificationProvider');
    }
    return context;
};
